import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useMemo, useState} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {useToast} from '@/components/ui/use-toast';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import type {
  DeleteResidentMedicationMutation,
  DosageDetails,
  MedicationForm,
  ResidentMedication,
  UpdateResidentMedicationMutation,
} from '@/services/graphqlHooks';
import {
  useDeleteResidentMedicationMutation,
  useGetResidentAllMedicationsQuery,
  useGetResidentCurrentMedicationsQuery,
  useGetResidentMedicationByIdQuery,
  useUpdateResidentMedicationMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';

import {
  addDosage,
  findDosage,
  removeDosage,
  updateDosage,
} from '../utils/dosageCrud';
import type {ResidentMedicationBasicFormValues} from '../utils/medicationFormInputs';
import {
  mapResidentMedicationBasicFormValuesToUpdateResidentMedicationInput,
  residentMedicationBasicFormDefaultValues,
  residentMedicationBasicFormSchema,
} from '../utils/medicationFormInputs';
import type {DosageDetailsWithId} from '../utils/medicationTypes';
import DosageDialog from './DosageDialog';
import ResidentMedicationBaseFormFields from './ResidentMedicationBaseFormFields';

const CREATE = 'create';

export type EditResidentMedicationDialogProps = DialogProps & {
  showResident?: boolean;
};

const EditResidentMedicationDialog = ({
  onOpenChange,
  showResident,
  ...props
}: EditResidentMedicationDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const {confirm} = useConfirm();
  const [searchParams] = useSearchParams();
  const [dosageId, setDosageId] = useState<string>();

  const id = searchParams.get('editMedication') || '';

  const canUpdateMedication = useAuthorize('UPDATE_MEDICATION');

  const {data: medicationData, isLoading: isMedicationLoading} =
    useGetResidentMedicationByIdQuery(graphqlClient, {id}, {enabled: !!id});

  const onSuccess = (residentId: string) => {
    queryClient.invalidateQueries({
      queryKey: useGetResidentCurrentMedicationsQuery.getKey({
        input: {
          residentId,
          date: dayjs().format(TIME_FORMATS.awsDate),
        },
      }),
    });

    queryClient.invalidateQueries({
      queryKey: useGetResidentAllMedicationsQuery.getKey({
        input: {
          residentId,
        },
      }),
    });

    queryClient.invalidateQueries({
      queryKey: ['GetAllResidentMedicationsForRange'],
    });
  };

  const deleteMedication = useDeleteResidentMedicationMutation(graphqlClient, {
    onSuccess: (data: DeleteResidentMedicationMutation) => {
      onSuccess(data?.deleteResidentMedication.residentId);
    },
  });

  const updateMedication = useUpdateResidentMedicationMutation(graphqlClient, {
    onSuccess: (data: UpdateResidentMedicationMutation) => {
      onSuccess(data.updateResidentMedication.residentId);
    },
  });

  const medication =
    medicationData?.getResidentMedicationById as ResidentMedication;

  const formValues = useMemo(
    () => residentMedicationBasicFormDefaultValues(medication),
    [medication]
  );

  const form = useForm<ResidentMedicationBasicFormValues>({
    resolver: zodResolver(residentMedicationBasicFormSchema),
    mode: 'onBlur',
    defaultValues: residentMedicationBasicFormDefaultValues(),
    values: formValues,
  });

  const handleSubmit = async (values: ResidentMedicationBasicFormValues) => {
    try {
      await updateMedication.mutateAsync({
        input:
          mapResidentMedicationBasicFormValuesToUpdateResidentMedicationInput(
            values,
            {id}
          ),
      });
      toast({
        title: t('Medication updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete medication'),
        message: t('Are you sure you want to delete this medication?'),
      });
      await deleteMedication.mutateAsync({id: medication?.id});
      toast({
        title: t('Medication deleted'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddDosage = (dosage: DosageDetails) => {
    const dosages = (form.getValues().dosageDetails ||
      []) as DosageDetailsWithId[];

    const newDosages =
      dosageId === CREATE
        ? addDosage(dosage, dosages)
        : updateDosage(
            {...dosage, id: dosageId} as DosageDetailsWithId,
            dosages
          );

    form.setValue('dosageDetails', newDosages, {shouldValidate: true});
  };

  const handleDeleteDosage = (id: string) => {
    const dosages = (form.getValues().dosageDetails ||
      []) as DosageDetailsWithId[];
    form.setValue('dosageDetails', removeDosage(id, dosages), {
      shouldValidate: true,
    });
  };

  const dosageToEdit = findDosage(
    dosageId || '',
    (form.getValues().dosageDetails || []) as DosageDetailsWithId[]
  );

  const selectedMedicationForm = useWatch({
    control: form.control,
    name: 'form',
  }) as MedicationForm | undefined;

  return (
    <>
      <Form {...form}>
        <Dialog {...props} onOpenChange={onOpenChange}>
          <DialogContent asChild>
            <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
              <DialogHeader>
                <DialogTitle>{t('Medication')}</DialogTitle>
              </DialogHeader>
              <DialogSection>
                {isMedicationLoading && <FormSkeleton />}
                {!isMedicationLoading && !medication && (
                  <div>{t('Medication not found')}</div>
                )}
                {medication && (
                  <ResidentMedicationBaseFormFields
                    key={medication.id}
                    showResident={showResident}
                    form={form}
                    onAddDosageClick={() => {
                      setDosageId(CREATE);
                    }}
                    onEditDosageClick={id => {
                      setDosageId(id);
                    }}
                    onDeleteDosageClick={handleDeleteDosage}
                  />
                )}
              </DialogSection>
              <DialogFooter>
                {!!medication && (
                  <>
                    <Button
                      type="button"
                      className="sm:mr-auto"
                      variant="warning"
                      onClick={() => handleDelete()}
                      disabled={
                        !canUpdateMedication || updateMedication.isPending
                      }
                    >
                      {t('Delete medication')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onOpenChange?.(false)}
                    >
                      {t('Close')}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        !canUpdateMedication || updateMedication.isPending
                      }
                    >
                      {t('Submit')}
                    </Button>
                  </>
                )}
                {!medication && (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                )}
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </Form>
      <DosageDialog
        key={dosageId}
        onSubmit={handleAddDosage}
        open={!!dosageId}
        dosageDetails={dosageToEdit}
        selectedMedicationForm={selectedMedicationForm}
        onOpenChange={() => {
          setDosageId('');
        }}
      />
    </>
  );
};

export default EditResidentMedicationDialog;
